// Pure display helpers for the admin section. Mirrors the backend's
// app/domain/{userStatus,classState}.js label maps for display purposes only —
// the backend remains authoritative for any filtering/aggregation logic.
// Mirrors the backend's app/domain/userStatus.js USER_STATUS codes, exported
// so activate/deactivate UI can send the correct numeric code without
// hardcoding 1000/4000 inline at each call site.
export const USER_STATUS = {
    ACTIVATED: 1000,
    DEACTIVATED: 4000,
};

const USER_STATUS_LABELS = {
    [USER_STATUS.ACTIVATED]: 'Active',
    [USER_STATUS.DEACTIVATED]: 'Deactivated',
};

const CLASS_STATE_LABELS = {
    1000: 'Active',
    2000: 'Done',
    3000: 'History',
    4000: 'Stopped',
};

// Preserves the raw country code (never lost) while displaying a human-readable
// name. Uses Intl.DisplayNames where available, with an explicit fallback map for
// the confirmed country codes seen in this dataset (covers environments/codes
// where Intl.DisplayNames can't resolve a name, or isn't available at all).
const COUNTRY_FALLBACK = {
    TR: 'Turkey',
    IQ: 'Iraq',
    HT: 'Haiti',
    SY: 'Syria',
    BR: 'Brazil',
    IR: 'Iran',
    SD: 'Sudan',
    PS: 'Palestine',
    JO: 'Jordan',
    DO: 'Dominican Republic',
    MA: 'Morocco',
    IL: 'Israel',
    EG: 'Egypt',
    AL: 'Albania',
    TM: 'Turkmenistan',
    LB: 'Lebanon',
    AF: 'Afghanistan',
    GA: 'Gabon',
    KR: 'South Korea',
};

let regionDisplayNames = null;
try {
    regionDisplayNames = new Intl.DisplayNames(['en'], { type: 'region' });
} catch (error) {
    regionDisplayNames = null;
}

export function userStatusLabel(code) {
    return USER_STATUS_LABELS[Number(code)] || 'Unknown';
}

export function classStateLabel(code) {
    return CLASS_STATE_LABELS[Number(code)] || 'Unknown';
}

export function countryName(code) {
    if (!code) return '';
    // The explicit fallback map takes priority over Intl.DisplayNames: it encodes
    // the exact names this dataset/legacy system already uses (e.g. 'PS' as
    // "Palestine", not Intl's "Palestinian Territories"), and it avoids Intl
    // resolving reserved/unassigned codes (e.g. 'ZZ') to a generic name like
    // "Unknown Region" instead of preserving the raw code.
    if (COUNTRY_FALLBACK[code]) return COUNTRY_FALLBACK[code];
    if (regionDisplayNames) {
        try {
            const name = regionDisplayNames.of(code);
            if (name && name !== code) return name;
        } catch (error) {
            // fall through to the raw code below
        }
    }
    return code;
}

// Turns a datetime coming back from the admin API into a Date, reading a bare
// "YYYY-MM-DD HH:mm:ss" as UTC.
//
// That reading is required, not a preference. The backend runs mysql2 with
// dateStrings:true and admin/classes.model.js returns classes.schedule verbatim,
// so what arrives here is the raw DB string — and the schedule column is stored
// in UTC (see the convention documented in the backend's classes.model.js).
//
// `new Date('2026-09-07 08:00:00')` does NOT parse that as UTC. The
// space-separated form falls outside the ES date-time grammar, so engines treat
// it as LOCAL time. The instant was therefore wrong by the viewer's own offset
// and then relabelled Eastern: in New York the raw UTC digits were displayed
// with an "EDT" suffix, and in Seoul the date slid to the previous evening.
export function parseUtcDateTime(value) {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    const text = String(value).trim();
    // Already carries an explicit zone (ISO 'Z', ±hh:mm, ±hhmm or ±hh) — that is
    // unambiguous, so let the engine parse it. The public /api/classes endpoints
    // normalize to ISO-Z via mysqlUtcToIsoZ, so both shapes really do reach the
    // admin UI. The zone must follow a clock time, or a date-only "…-09-07"
    // would read its day as a -07 offset. ±hh and ±hhmm aren't ES date-time
    // grammar, so they are rewritten to ±hh:mm before parsing.
    const zoneMatch = text.match(/^(.*\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)\s*(Z|([+-]\d{2}):?(\d{2})?)$/i);
    if (zoneMatch) {
        const [, clock, , offsetHours, offsetMinutes] = zoneMatch;
        const offset = offsetHours ? `${offsetHours}:${offsetMinutes || '00'}` : 'Z';
        const zoned = new Date(`${clock.replace(' ', 'T')}${offset}`);
        return Number.isNaN(zoned.getTime()) ? null : zoned;
    }

    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (match) {
        const [, year, month, day, hour, minute, second] = match;
        return new Date(Date.UTC(
            Number(year), Number(month) - 1, Number(day),
            Number(hour), Number(minute), Number(second || 0)
        ));
    }

    const fallback = new Date(text);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
}

const SCHEDULE_FORMAT = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
};

// No manual offset arithmetic — always goes through Intl.DateTimeFormat, so DST
// is whatever the zone database says for that particular date.
function formatScheduleInZone(value, timeZone) {
    const date = parseUtcDateTime(value);
    if (!date) return value ? String(value) : '';
    try {
        return new Intl.DateTimeFormat('en-US', { timeZone, ...SCHEDULE_FORMAT }).format(date);
    } catch (error) {
        return date.toISOString();
    }
}

// Eastern remains the primary operational display.
export function formatScheduleEastern(value) {
    return formatScheduleInZone(value, 'America/New_York');
}

// Shown alongside Eastern so the stored value is verifiable at a glance —
// without it there is no way to tell a correct conversion from the old bug.
export function formatScheduleUtc(value) {
    return formatScheduleInZone(value, 'UTC');
}

// Parses a "list-ish" DB field that may come back as a JSON array string, a
// plain comma-separated string, or already an array (mirrors the parsing
// TeacherDetail.vue/StudentDetail.vue already did locally for availableTimes —
// centralized here so it's not duplicated per view).
export function parseListField(value) {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.trim()) {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [parsed];
        } catch (error) {
            return value.split(',').map((item) => item.trim()).filter(Boolean);
        }
    }
    return [];
}

// Formats users.studentCountryPreferences for display. Routes each parsed
// token through countryName() — safe even if the underlying values turn out
// to be free text rather than ISO codes, since countryName() falls back to
// returning its input unchanged when it can't resolve a name.
export function formatCountryPreferences(raw) {
    const codes = parseListField(raw);
    if (!codes.length) return '—';
    return codes.map((code) => countryName(code)).join(', ');
}

export function formatCompletionRate(completed, total) {
    const completedNum = Number(completed) || 0;
    const totalNum = Number(total) || 0;
    if (totalNum === 0) return '—';
    return `${Math.round((completedNum / totalNum) * 100)}%`;
}

// Only http(s) links may become clickable hrefs. Video links are entered by
// teachers, so a `javascript:` URL would otherwise run on the admin origin.
export function isSafeUrl(url) {
    if (typeof url !== 'string') return false;
    return /^https?:\/\//i.test(url.trim());
}
